import { ActionTable, PermissionCheck } from '@/components';
import {
  FormComboBox,
  FormController,
  FormInput,
  FormTextArea,
} from '@/components/form';
import { CoreApi, ProcurementApi, ReleasesApi } from '@/state/api';
import { ProcurementType } from '@/types';
import { permissionsKeys } from '@/types/permissionsType';
import {
  Box,
  Button,
  Center,
  Flex,
  Grid,
  GridItem,
  Separator,
  Text,
} from '@chakra-ui/react';
import { skipToken } from '@reduxjs/toolkit/query';
import { Card, OverlayDialog, toasterApi } from '@ui';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import {
  Control,
  FieldErrors,
  useFieldArray,
  UseFieldArrayRemove,
  UseFormRegister,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';
import { useSelector } from 'react-redux';
import z from 'zod';
import {
  buildAdditionalPurchaseOrderLinesPayload,
  newPoLinesFormSchema,
  NewPoLinesFormType,
} from './purchaseOrderLineForm';

type EditPurchaseOrderLinesProps = {
  purchaseOrderId?: number | null;
  onClose: () => void;
};

const EditPurchaseOrderLines = ({
  purchaseOrderId,
  onClose,
}: EditPurchaseOrderLinesProps) => {
  const [selectedPurchaseOrderLine, setSelectedPurchaseOrderLine] =
    useState<PurchaseOrderLineTableType | null>(null);

  // Purchase order query
  const { data: purchaseOrder, isLoading: isLoadingPurchaseOrder } =
    ProcurementApi.Api.useGetPurchaseOrderQuery(
      purchaseOrderId
        ? {
            params: {
              id: purchaseOrderId,
            },
          }
        : skipToken,
    );

  // Purchase order line patch mutation
  const [patchPurchaseOrderLine] =
    ProcurementApi.Api.usePatchPurchaseOrderLineMutation();
  const [patchPurchaseOrder, { isLoading: isSavingHeader }] =
    ProcurementApi.Api.usePatchPurchaseOrderMutation();
  const { data: suppliers, isLoading: isLoadingSuppliers } =
    ProcurementApi.Api.useGetAllSuppliersQuery({});

  // Delete a single PO line. Backend rejects unless the PO is DRAFT_PO,
  // line.qty_received === 0, and no InventoryReceipt is linked. We mirror
  // the first two guards in the UI (status + qty_received) so the action
  // is hidden when it would always 400. The third guard only triggers if
  // backend state changed between the page load and click; we let the
  // toast surface that as an error.
  const [deletePurchaseOrderLine, { isLoading: isDeletingLine }] =
    ProcurementApi.Api.useDeletePurchaseOrderLineMutation();

  // Pull the user's permissions synchronously from auth state so we can
  // gate the actions column without an async checkPermission round-trip.
  // Mirrors the selector pattern in PermissionCheck.
  const userPermissions = useSelector(
    (state: any) => state.auth?.current?.user?.permissions || [],
  ) as string[];
  const canChangePO = userPermissions.includes(
    permissionsKeys.change_purchaseorder,
  );

  // Show the actions column only when deletion is even theoretically
  // possible: PO must be in DRAFT_PO and the user must have
  // change_purchaseorder. Per-row qty_received gating happens inside
  // renderCell so post-receiving lines still render an empty cell instead
  // of a button that 400s.
  const isDraftPo = purchaseOrder?.status === 'DRAFT_PO';
  const showDeleteActions = isDraftPo && canChangePO;

  const currentLine =
    purchaseOrder?.lines?.find(
      (line) => line.id === selectedPurchaseOrderLine?.id,
    ) ?? purchaseOrder?.lines?.[0];

  // Determine manual vs system for display/edit behavior
  const partId =
    (currentLine as any)?.part ?? (currentLine as any)?.part_id ?? null;
  const partNumberSnapshot = String(
    (currentLine as any)?.part_number_snapshot ?? '',
  ).trim();
  // If there is no system part id, treat as manual
  const isManualLine = !partId;

  // Open the standard cancel-style confirmation modal before issuing the
  // DELETE. Mirrors handleCancelPurchaseOrder in PurchaseOrdersTable so
  // the UX is consistent with the rest of the procurement screens.
  const handleDeleteLine = (line: PurchaseOrderLineTableType) => {
    const partLabel =
      String((line as any)?.part_number_snapshot ?? '').trim() ||
      String((line as any)?.manual_part_number ?? '').trim() ||
      `#${line.id}`;
    OverlayDialog.open('deletePurchaseOrderLine', {
      overlayId: 'deletePurchaseOrderLine',
      title: 'Delete purchase order line',
      content: (
        <Flex flexDirection="column" gapY={2}>
          <Text>
            Are you sure you want to delete line <b>{partLabel}</b> (qty{' '}
            {line.qty_ordered ?? 0}) from this draft purchase order?
          </Text>
          <Text color="fg.muted" fontSize="sm">
            This action is irreversible.
          </Text>
        </Flex>
      ),
      handleConfirm: () => {
        deletePurchaseOrderLine({
          params: { id: line.id! },
        })
          .unwrap()
          .then(() => {
            // Clear the per-line edit form if it was pointing at the row
            // we just removed; otherwise the right-hand panel keeps trying
            // to render a stale id.
            if (selectedPurchaseOrderLine?.id === line.id) {
              setSelectedPurchaseOrderLine(null);
            }
            toasterApi({
              type: 'success',
              status: 200,
              code: 'Success',
              message: 'Purchase order line deleted successfully',
            });
          })
          .catch((err: any) => {
            // Surface the backend's reason verbatim when present (e.g.
            // "Cannot delete a purchase order line after receiving has
            // started.") so the user understands which guard tripped.
            const detail =
              err?.data?.detail ||
              (Array.isArray(err?.data?.detail)
                ? err.data.detail.join(' ')
                : null) ||
              'Could not delete purchase order line';
            toasterApi({
              type: 'error',
              status: err?.status || 500,
              code: 'Error',
              message: detail,
            });
          })
          .finally(() => {
            OverlayDialog.close('deletePurchaseOrderLine');
          });
      },
    });
  };

  // Extend the static columns config with an actions column only when
  // deletion is allowed for this PO. Memoize on showDeleteActions so the
  // ActionTable doesn't see a fresh array every parent render when the
  // PO is past DRAFT_PO (the common case for already-issued POs).
  const tableColumns = useMemo<
    ActionTable.ColumnsConfigType<PurchaseOrderLineTableType>
  >(() => {
    if (!showDeleteActions) return columns;
    return [
      ...columns,
      { actions: { label: 'Actions', width: '100px' } } as any,
    ];
  }, [showDeleteActions]);

  // Render the actions cell inline so it can close over the delete
  // handler and the in-flight state. For all other columns we delegate
  // back to the module-level renderCell so the existing rendering
  // (created_at/needed_by formatting, part snapshot fallback, etc.) is
  // preserved exactly.
  const tableRenderCell = (
    props: ActionTable.RenderCellType<PurchaseOrderLineTableType>,
  ) => {
    // The "actions" column is appended dynamically and is not part of the
    // statically-typed PurchaseOrderLineTableType keys, so we have to
    // compare against a string literal cast here.
    if ((props.colKey as string) === 'actions') {
      const row = props.rowValue as
        | (PurchaseOrderLineTableType & { qty_received?: number })
        | undefined;
      // Per-row guard mirroring backend Guard 2: never offer delete for a
      // line that has already been received against. The backend would
      // 400, so hiding the button avoids a confusing toast.
      if (!row || (row.qty_received ?? 0) > 0) {
        return <Text color="fg.muted">—</Text>;
      }
      return (
        <Button
          size="xs"
          variant="outline"
          colorPalette="red"
          loading={isDeletingLine}
          onClick={(e) => {
            // The row is also clickable to load it into the edit form;
            // stop propagation so opening the confirm dialog doesn't
            // also reset the edit panel.
            e.stopPropagation();
            handleDeleteLine(row);
          }}
        >
          Delete
        </Button>
      );
    }
    return renderCell(props);
  };

  return (
    <Flex flexDirection="column" gapY={4}>
      <Card>
        <Flex flexDirection="column" gapY={4}>
          <Flex justifyContent="space-between">
            <Flex flexDirection="column" gapY={2}>
              <Text fontWeight="bold">
                {purchaseOrder?.project_po_number ??
                  purchaseOrder?.po_number ??
                  `PO #${purchaseOrderId}`}{' '}
                — Edit Purchase Order Lines
              </Text>
              <Flex gapX={4} flexWrap="wrap" fontSize="sm" color="fg.subtle">
                {purchaseOrder?.title && (
                  <Text>Title: {purchaseOrder.title}</Text>
                )}
                {purchaseOrder?.function_number && (
                  <Text>Function: {purchaseOrder.function_number}</Text>
                )}
              </Flex>
            </Flex>
            <Button size="sm" bg="fg" color="fg.inverted" onClick={onClose}>
              Back
            </Button>
          </Flex>

          {purchaseOrder && isDraftPo && canChangePO && (
            <>
              <Separator />
              <FormController
                key={`edit-purchase-order-header-${purchaseOrder.id}-${purchaseOrder.updated_at}`}
                size="lg"
                formSchema={
                  ProcurementType.PurchaseOrders.patchPurchaseOrder.body
                }
                defaultValues={{
                  supplier: purchaseOrder.supplier,
                  title: purchaseOrder.title ?? '',
                  function_number: purchaseOrder.function_number ?? '',
                  expected_delivery_date:
                    purchaseOrder.expected_delivery_date &&
                    dayjs(purchaseOrder.expected_delivery_date).isValid()
                      ? dayjs(purchaseOrder.expected_delivery_date).format(
                          'YYYY-MM-DD',
                        )
                      : '',
                  shipping_address: purchaseOrder.shipping_address ?? '',
                  supplier_reference: purchaseOrder.supplier_reference ?? '',
                }}
                buttonProps={{ hidden: true }}
                onFormSubmit={async (formData) => {
                  try {
                    await patchPurchaseOrder({
                      params: { id: purchaseOrder.id },
                      body: {
                        supplier: formData.supplier ?? purchaseOrder.supplier,
                        title: formData.title?.trim() || null,
                        function_number:
                          formData.function_number?.trim() || null,
                        expected_delivery_date:
                          formData.expected_delivery_date &&
                          dayjs(formData.expected_delivery_date).isValid()
                            ? dayjs(
                                formData.expected_delivery_date,
                              ).toISOString()
                            : null,
                        shipping_address:
                          formData.shipping_address?.trim() || null,
                        supplier_reference:
                          formData.supplier_reference?.trim() || null,
                      },
                    }).unwrap();
                    toasterApi({
                      type: 'success',
                      status: 200,
                      code: 'Success',
                      message: 'Purchase order header updated successfully',
                    });
                  } catch (error) {
                    toasterApi({
                      type: 'error',
                      status: getApiErrorStatus(error),
                      code: 'Error',
                      message: getApiErrorDetail(
                        error,
                        'Could not update purchase order header',
                      ),
                    });
                  }
                }}
              >
                {({ register, control, errors }) => (
                  <Flex flexDirection="column" gapY={3}>
                    <Text fontSize="sm" fontWeight="semibold">
                      Draft Header
                    </Text>
                    <Grid templateColumns="repeat(4, minmax(0, 1fr))" gap={3}>
                      <GridItem>
                        <FormComboBox
                          register={register}
                          control={control}
                          errors={errors}
                          id="supplier"
                          label="Supplier"
                          keyForDisplay="name"
                          keyForValue="id"
                          items={suppliers ?? []}
                          showLabelAtTop
                          required
                          clearable={false}
                          disabled={isLoadingSuppliers}
                          isLoading={isLoadingSuppliers}
                          helperText="Type any part of the company name, then select an existing supplier."
                        />
                      </GridItem>
                      <GridItem>
                        <FormInput
                          register={register}
                          control={control}
                          errors={errors}
                          id="title"
                          label="Title"
                          showLabelAtTop
                        />
                      </GridItem>
                      <GridItem>
                        <FormInput
                          register={register}
                          control={control}
                          errors={errors}
                          id="function_number"
                          label="Function Number"
                          showLabelAtTop
                        />
                      </GridItem>
                      <GridItem>
                        <FormInput
                          register={register}
                          control={control}
                          errors={errors}
                          id="expected_delivery_date"
                          label="Expected Delivery Date"
                          type="date"
                          showLabelAtTop
                        />
                      </GridItem>
                      <GridItem>
                        <FormInput
                          register={register}
                          control={control}
                          errors={errors}
                          id="supplier_reference"
                          label="Supplier Quote Number"
                          showLabelAtTop
                        />
                      </GridItem>
                      <GridItem colSpan={2}>
                        <FormInput
                          register={register}
                          control={control}
                          errors={errors}
                          id="shipping_address"
                          label="Shipping Address"
                          showLabelAtTop
                        />
                      </GridItem>
                    </Grid>
                    <Button
                      type="submit"
                      size="sm"
                      variant="outline"
                      alignSelf="flex-end"
                      loading={isSavingHeader}
                    >
                      Save Header
                    </Button>
                  </Flex>
                )}
              </FormController>
            </>
          )}
        </Flex>
      </Card>

      <Flex flexDirection="column" gapY={4}>
        <Flex flex={1}>
          <Card>
            <Flex flexDirection="column" gapY={2}>
              <Text fontSize="sm" fontWeight="bold">
                Purchase Order Lines
              </Text>

              <Center>
                <Box maxWidth="fit-content">
                  <Flex flexDirection="column" gapY={2}>
                    <ActionTable.FilterBar
                      filterConfigComponents={filterConfigComponents}
                    />
                    <Box display="flex" flexDirection="row" gap={5}>
                      <Box h="100%" my={5}>
                        <ActionTable<PurchaseOrderLineTableType>
                          width="76vw"
                          columns={tableColumns}
                          columnToUseAsRowKey="id"
                          items={purchaseOrder?.lines ?? []}
                          isPaginated={false}
                          isPaginatedAsync={false}
                          totalItemsCount={purchaseOrder?.lines?.length ?? 0}
                          isSortable={true}
                          isFilterable={true}
                          isShowActionBar={false}
                          height={'35vh'}
                          rowHeight={50}
                          isLoading={isLoadingPurchaseOrder}
                          isMultipleSelection={false}
                          renderCell={tableRenderCell}
                          onRowClick={(row) =>
                            setSelectedPurchaseOrderLine(row)
                          }
                        />
                      </Box>
                    </Box>
                  </Flex>
                </Box>
              </Center>
            </Flex>
          </Card>
        </Flex>

        <Flex flex={1}>
          <Card>
            <Flex flexDirection="column" gapY={4}>
              <Flex flexDirection="column" gapY={1}>
                <Text fontSize="sm" fontWeight="bold">
                  Line Details
                </Text>
                <Flex fontWeight="bold" alignItems="center">
                  <Text>Line {currentLine?.id}</Text>
                </Flex>
              </Flex>

              <Flex>
                <FormController
                  key={`edit-purchase-order-line-${currentLine?.id}`}
                  size="lg"
                  formSchema={ProcurementType.PurchaseOrderLines.patch.body}
                  disabled={!isDraftPo || !currentLine}
                  defaultValues={{
                    manual_part_number: isManualLine
                      ? partNumberSnapshot
                      : undefined,
                    description: isManualLine
                      ? ((currentLine as any)?.part_description_snapshot ?? '')
                      : undefined,
                    needed_by:
                      currentLine?.needed_by &&
                      dayjs(currentLine.needed_by).isValid()
                        ? dayjs(currentLine.needed_by).format('YYYY-MM-DD')
                        : '',
                    qty_ordered: currentLine?.qty_ordered ?? 0,
                    unit_price: currentLine?.unit_price ?? '',
                    notes: currentLine?.notes ?? '',
                  }}
                  onFormSubmit={async (formData) => {
                    // Normalize values so the DRF serializer always receives
                    // the exact types it expects:
                    //   qty_ordered -> PositiveIntegerField (int)
                    //   unit_price  -> DecimalField (numeric string)
                    //   needed_by   -> DateTimeField ("YYYY-MM-DD")
                    const qtyRaw = formData.qty_ordered as unknown as
                      | number
                      | string
                      | undefined;
                    const qtyNum =
                      qtyRaw === undefined || qtyRaw === ''
                        ? undefined
                        : Number(qtyRaw);

                    const priceRaw = formData.unit_price as unknown as
                      | number
                      | string
                      | undefined;
                    const priceStr =
                      priceRaw === undefined || priceRaw === ''
                        ? undefined
                        : String(priceRaw);

                    // needed_by is optional. An empty/invalid value must be
                    // persisted as null (DRF DateTimeField rejects ""), and the
                    // table renders null as "Not set".
                    const neededByRaw = (formData.needed_by ?? '') as string;
                    const neededByVal =
                      neededByRaw && dayjs(neededByRaw).isValid()
                        ? dayjs(neededByRaw).format('YYYY-MM-DD')
                        : null;

                    const body: any = {
                      needed_by: neededByVal,
                      ...(qtyNum !== undefined &&
                        Number.isFinite(qtyNum) && { qty_ordered: qtyNum }),
                      ...(priceStr !== undefined && { unit_price: priceStr }),
                      notes: formData.notes,
                    };

                    if (isManualLine) {
                      body.manual_part_number = String(
                        formData.manual_part_number ?? '',
                      ).trim();
                      body.description =
                        String(formData.description ?? '').trim() || null;
                    }

                    try {
                      await patchPurchaseOrderLine({
                        body,
                        params: {
                          id: currentLine?.id!,
                        },
                      }).unwrap();
                      toasterApi({
                        type: 'success',
                        status: 200,
                        code: 'Success',
                        message: 'Purchase order line updated successfully',
                      });
                      setSelectedPurchaseOrderLine(null);
                    } catch (error) {
                      toasterApi({
                        type: 'error',
                        status: getApiErrorStatus(error),
                        code: 'Error',
                        message: getApiErrorDetail(
                          error,
                          'Could not update purchase order line',
                        ),
                      });
                    }
                  }}
                  buttonProps={{
                    hidden: true,
                  }}
                >
                  {({
                    register,
                    control,
                    errors,
                  }: {
                    register: UseFormRegister<
                      ProcurementType.PurchaseOrderLines.Patch['body']
                    >;
                    control: Control<
                      ProcurementType.PurchaseOrderLines.Patch['body']
                    >;
                    errors: FieldErrors<
                      ProcurementType.PurchaseOrderLines.Patch['body']
                    >;
                  }) => {
                    return (
                      <Flex
                        flexDirection="column"
                        gapY={4}
                        width="full"
                        overflowX="hidden"
                      >
                        {/* ✅ One-row layout */}
                        <Grid
                          templateColumns="repeat(4, 1fr)"
                          gap={4}
                          alignItems="end"
                          width="full"
                        >
                          {/* Linked parts remain read-only; manual lines use aliases. */}
                          <GridItem>
                            <FormInput
                              width="full"
                              register={register}
                              control={control}
                              errors={errors}
                              id="manual_part_number"
                              label={
                                isManualLine ? 'Item / Service' : 'Linked Part'
                              }
                              showLabelAtTop
                              placeholder={partNumberSnapshot || '—'}
                              value={
                                isManualLine ? undefined : partNumberSnapshot
                              }
                              disabled={!isManualLine}
                            />
                          </GridItem>

                          {/* Needed by */}
                          <GridItem>
                            <FormInput
                              width="full"
                              register={register}
                              control={control}
                              errors={errors}
                              id="needed_by"
                              label="Needed By"
                              showLabelAtTop
                              type="date"
                            />
                          </GridItem>

                          {/* Quantity */}
                          <GridItem>
                            <FormInput
                              width="full"
                              defaultValue={currentLine?.qty_ordered ?? 1}
                              register={register}
                              control={control}
                              errors={errors}
                              id="qty_ordered"
                              label="Quantity"
                              showLabelAtTop
                              min={1}
                              type="number"
                            />
                          </GridItem>

                          {/* Unit Price */}
                          <GridItem>
                            <Box width="200px">
                              <FormInput
                                width="200px"
                                register={register}
                                control={control}
                                errors={errors}
                                id="unit_price"
                                label="Unit Price"
                                showLabelAtTop
                                type="number"
                                min={0}
                                step="0.01"
                              />
                            </Box>
                          </GridItem>

                          {isManualLine && (
                            <GridItem colSpan={4}>
                              <FormTextArea
                                register={register}
                                control={control}
                                errors={errors}
                                id="description"
                                label="Description"
                                showLabelAtTop
                                minH="2lh"
                                maxH="2lh"
                              />
                            </GridItem>
                          )}

                          <GridItem colSpan={4}>
                            <FormTextArea
                              register={register}
                              control={control}
                              errors={errors}
                              id="notes"
                              label="Notes (Optional)"
                              showLabelAtTop
                              minH="2lh"
                              maxH="2lh"
                            />
                          </GridItem>
                        </Grid>

                        <Flex justifyContent="end" gapX={2}>
                          <Button
                            variant="outline"
                            color="fg"
                            size="sm"
                            borderColor="fg"
                            type="submit"
                            disabled={!isDraftPo || !currentLine}
                          >
                            Save Line
                          </Button>
                        </Flex>
                      </Flex>
                    );
                  }}
                </FormController>
              </Flex>
            </Flex>
          </Card>
        </Flex>

        {/*
         * Add new lines to an existing PO. Backend only allows new lines
         * while status === 'DRAFT_PO' (PurchaseOrder.assert_editable). We
         * mirror that gate here so users never see a control the server
         * will refuse, and we additionally gate behind change_purchaseorder
         * to match the existing line-edit permission.
         */}
        {purchaseOrder?.status === 'DRAFT_PO' && (
          <PermissionCheck permissions={[permissionsKeys.change_purchaseorder]}>
            <Flex flex={1}>
              <AddPurchaseOrderLinesPanel
                purchaseOrderId={purchaseOrder.id}
                projectId={purchaseOrder.project as number}
              />
            </Flex>
          </PermissionCheck>
        )}
      </Flex>
    </Flex>
  );
};

export default EditPurchaseOrderLines;

const purchaseOrderLineSchema =
  ProcurementType.PurchaseOrders.getPurchaseOrder.response.shape.lines.element;
type PurchaseOrderLineTableType = z.infer<typeof purchaseOrderLineSchema>;

const columns: ActionTable.ColumnsConfigType<PurchaseOrderLineTableType> = [
  { id: { label: 'Id', width: '50px' } },
  { po: { label: 'PO', width: '50px' } },
  { created_at: { label: 'Created At', width: '100px' } },
  { needed_by: { label: 'Needed By', width: '100px' } },
  { part: { label: 'Part', width: '80px' } },
  { qty_ordered: { label: 'Quantity Ordered', width: '80px' } },
  { unit_price: { label: 'Unit Price', width: '100px' } },
  { part_number_snapshot: { label: 'Part Number Snapshot', width: '120px' } },
  {
    part_description_snapshot: {
      label: 'Part Description Snapshot',
      width: '100px',
    },
  },
  { notes: { label: 'Notes', width: '150px' } },
];

const renderCell = (
  props: ActionTable.RenderCellType<PurchaseOrderLineTableType>,
) => {
  const { colKey, cellValue, rowValue } = props;

  switch (colKey) {
    case 'created_at':
      return (
        <Text>
          {cellValue && dayjs(cellValue).isValid()
            ? dayjs(cellValue).format('DD MMM YYYY')
            : '—'}
        </Text>
      );
    // needed_by is nullable (e.g. imported POs whose source ship date is "TBD").
    // Never feed a null/blank value to dayjs().format() — it yields the literal
    // string "Invalid Date".
    case 'needed_by':
      return (
        <Text>
          {cellValue && dayjs(cellValue).isValid()
            ? dayjs(cellValue).format('DD MMM YYYY')
            : 'Not set'}
        </Text>
      );
    // The "part" column on the wire is the integer FK to the Part model.
    // Showing the raw id (e.g. 192) is unhelpful — render the persisted
    // part_number_snapshot instead, which is populated for both manual and
    // system (release) lines. Fall back to the manual_part_number, then to
    // the raw id, then to "—".
    case 'part': {
      const snapshot = String(
        (rowValue as any)?.part_number_snapshot ?? '',
      ).trim();
      const manual = String((rowValue as any)?.manual_part_number ?? '').trim();
      return <Text>{snapshot || manual || cellValue || '—'}</Text>;
    }
    default:
      return cellValue?.value ?? cellValue ?? '';
  }
};

const filterConfigComponents: ActionTable.FilterConfigComponentType<PurchaseOrderLineTableType>[] =
  [
    {
      filterComponent: 'input',
      filterComponentLabel: 'PO',
      filterConfigItems: [{ colKeys: ['po'], value: '', operator: 'contains' }],
    },
    {
      filterComponent: 'input',
      filterComponentLabel: 'Part Number Snapshot',
      filterConfigItems: [
        { colKeys: ['part_number_snapshot'], value: '', operator: 'contains' },
      ],
    },
  ];

// ─────────────────────────────────────────────────────────────────────────────
// Add new PO lines panel
// ─────────────────────────────────────────────────────────────────────────────
//
// Manual-first add-line flow. BOM releases and parts are loaded only after a
// row switches to linked mode, then shared across all rows in this panel.

type AddPurchaseOrderLinesPanelProps = {
  purchaseOrderId: number;
  projectId: number;
};

const AddPurchaseOrderLinesPanel = ({
  purchaseOrderId,
  projectId,
}: AddPurchaseOrderLinesPanelProps) => {
  const [postPurchaseOrderLines, { isLoading: isSavingNewLines }] =
    ProcurementApi.Api.usePostPurchaseOrderLinesMutation();

  // Bumping this on successful save resets the entire form (including the
  // useFieldArray rows) to the empty default — no stale rows linger after
  // saving and the table refreshes from the invalidated getPurchaseOrder
  // query.
  const [formInstanceId, setFormInstanceId] = useState(0);

  return (
    <Card>
      <Flex flexDirection="column" gapY={4}>
        <Flex justifyContent="space-between" alignItems="center">
          <Text fontSize="sm" fontWeight="bold">
            Add New Lines
          </Text>
        </Flex>

        <FormController
          key={`add-po-lines-${purchaseOrderId}-${formInstanceId}`}
          size="lg"
          formSchema={newPoLinesFormSchema}
          defaultValues={{ newLines: [] }}
          onFormSubmit={async (formData: NewPoLinesFormType) => {
            const rows = formData.newLines ?? [];
            if (rows.length === 0) return;

            const body = buildAdditionalPurchaseOrderLinesPayload(rows);

            try {
              const response = await postPurchaseOrderLines({
                params: { po_id: purchaseOrderId },
                body: body as ProcurementType.PurchaseOrderLines.Post['body'],
              }).unwrap();

              const partialErrors = response?.errors;
              if (partialErrors && partialErrors.length > 0) {
                // Backend returned 207: some rows saved, some failed. Surface
                // the per-row failure messages so the user can fix them.
                toasterApi({
                  type: 'error',
                  status: 207,
                  code: 'Partial Success',
                  message:
                    partialErrors.map((e) => e.detail).join('\n') ||
                    'Some lines could not be added to the purchase order',
                });
              } else {
                toasterApi({
                  type: 'success',
                  status: 200,
                  code: 'Success',
                  message: 'New purchase order lines added successfully',
                });
              }

              // Reset the form back to empty so users don't accidentally
              // re-submit the same rows. The successful rows are now in the
              // PO and will appear in the table once getPurchaseOrder
              // refetches via tag invalidation.
              setFormInstanceId((prev) => prev + 1);
            } catch (error) {
              toasterApi({
                type: 'error',
                status: getApiErrorStatus(error),
                code: 'Error',
                message: getApiErrorDetail(
                  error,
                  'Could not add new purchase order lines',
                ),
              });
            }
          }}
          buttonProps={{ hidden: true }}
        >
          {({
            register,
            control,
            errors,
            setValue,
            trigger,
          }: {
            register: UseFormRegister<NewPoLinesFormType>;
            control: Control<NewPoLinesFormType>;
            errors: FieldErrors<NewPoLinesFormType>;
            setValue?: UseFormReturn<NewPoLinesFormType>['setValue'];
            trigger?: UseFormReturn<NewPoLinesFormType>['trigger'];
          }) => {
            const { fields, append, remove } = useFieldArray({
              control,
              name: 'newLines',
            });

            const newLines = useWatch({ control, name: 'newLines' });
            const hasLinkedLines =
              newLines?.some((line) => line.part_type === 'system') ?? false;
            const { data: projectParts } =
              CoreApi.Api.useGetPartsListByProjectAndReleaseQuery(
                hasLinkedLines
                  ? { query: { project: projectId, release: undefined } }
                  : skipToken,
              );
            const { data: allReleases } = ReleasesApi.Api.useGetAllReleaseQuery(
              undefined,
              {
                skip: !hasLinkedLines,
              },
            );
            const releasesForProject =
              allReleases?.filter((release) => release.project === projectId) ??
              [];

            return (
              <Flex flexDirection="column" gapY={4} width="full">
                <Flex justifyContent="space-between" alignItems="center">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    color="fg"
                    borderColor="fg"
                    onClick={() =>
                      append({
                        part_type: 'manual',
                        part: undefined,
                        manual_part_number: '',
                        description: '',
                        qty: 1,
                        unit_price: '',
                        needed_by: '',
                        promised_date: '',
                        notes: '',
                        release: undefined,
                      })
                    }
                  >
                    Add line
                  </Button>

                  <Button
                    size="sm"
                    type="submit"
                    bg="fg"
                    color="fg.inverted"
                    disabled={
                      isSavingNewLines || !newLines || newLines.length === 0
                    }
                  >
                    {isSavingNewLines ? 'Saving…' : 'Save New Lines'}
                  </Button>
                </Flex>

                {fields.length === 0 ? (
                  <Text fontSize="sm" color="fg.muted">
                    Click <b>Add line</b> to add new lines to this purchase
                    order. New lines are saved together when you click{' '}
                    <b>Save New Lines</b>.
                  </Text>
                ) : (
                  <Flex flexDirection="column" gapY={2}>
                    {fields.map((field, index) => (
                      <Flex
                        key={`new-po-line-${field.id}`}
                        flexDirection="column"
                      >
                        <NewPoLineRow
                          register={register}
                          control={control}
                          errors={errors}
                          index={index}
                          newLines={newLines}
                          remove={remove}
                          setValue={setValue}
                          trigger={trigger}
                          releases={releasesForProject}
                          parts={projectParts ?? []}
                        />
                        {index !== fields.length - 1 && (
                          <Separator marginY={3} />
                        )}
                      </Flex>
                    ))}
                  </Flex>
                )}
              </Flex>
            );
          }}
        </FormController>
      </Flex>
    </Card>
  );
};

type NewPoLineRowProps = {
  register: UseFormRegister<NewPoLinesFormType>;
  control: Control<NewPoLinesFormType>;
  errors: FieldErrors<NewPoLinesFormType>;
  index: number;
  newLines: NewPoLinesFormType['newLines'] | undefined;
  remove: UseFieldArrayRemove;
  setValue?: UseFormReturn<NewPoLinesFormType>['setValue'];
  trigger?: UseFormReturn<NewPoLinesFormType>['trigger'];
  releases: { id: number; number: string | number; project: number }[];
  parts: { id: number; part_number: string }[];
};

const NewPoLineRow = ({
  register,
  control,
  errors,
  index,
  newLines,
  remove,
  setValue,
  trigger,
  releases,
  parts,
}: NewPoLineRowProps) => {
  const partType = newLines?.[index]?.part_type;

  // Hide parts already chosen on other rows in this batch for the same
  // release, mirroring the create-flow guard against picking the same part
  // twice. Lines already saved on the PO are not in this list because the
  // backend's unique_together (po, part) catches that case and returns a
  // friendly 207 error per row.
  const partsSelectedInOtherRows =
    newLines
      ?.filter((_, i) => i !== index)
      ?.filter((line) => line?.part_type === 'system')
      ?.map((line) => line?.part)
      ?.filter((v): v is number => v !== undefined && v > 0) ?? [];
  const excludedParts = new Set<number>(partsSelectedInOtherRows);

  const filteredParts = parts.filter((part) => !excludedParts.has(part.id));

  return (
    <Flex width="full" flexDirection="column" gapY={3}>
      <Flex justifyContent="space-between" alignItems="center">
        <Text fontWeight="bold" color="tertiary.fg">
          New Line {index + 1}
        </Text>
        <Flex gapX={2}>
          <Button
            type="button"
            size="xs"
            variant="outline"
            onClick={() => {
              if (!setValue || !trigger) return;
              const nextPartType = partType === 'system' ? 'manual' : 'system';
              setValue(`newLines.${index}.part_type`, nextPartType, {
                shouldDirty: true,
                shouldValidate: true,
              });
              setValue(`newLines.${index}.part`, undefined, {
                shouldDirty: true,
              });
              setValue(`newLines.${index}.release`, undefined, {
                shouldDirty: true,
              });
              setValue(`newLines.${index}.manual_part_number`, '', {
                shouldDirty: true,
              });
              setValue(`newLines.${index}.description`, '', {
                shouldDirty: true,
              });
              trigger([
                `newLines.${index}.part_type`,
                `newLines.${index}.part`,
                `newLines.${index}.manual_part_number`,
              ] as any);
            }}
          >
            {partType === 'system' ? 'Use manual item' : 'Link BOM item'}
          </Button>
          <Button
            type="button"
            size="xs"
            variant="outline"
            colorPalette="red"
            onClick={() => remove(index)}
          >
            Remove
          </Button>
        </Flex>
      </Flex>

      <Flex gap={4} flexWrap="wrap">
        {partType === 'system' ? (
          <>
            <Box position="relative" maxW="250px">
              <FormComboBox
                maxW="250px"
                register={register}
                control={control}
                errors={errors}
                id={`newLines.${index}.release`}
                label="Release (optional)"
                keyForDisplay="number"
                keyForValue="id"
                items={releases}
                showLabelAtTop
                clearable
              />
              <Text
                position="absolute"
                bottom={-2}
                fontSize="xs"
                color="fg.error"
              >
                {errors?.newLines?.[index]?.release?.message}
              </Text>
            </Box>
            <Box
              key={`new-line-system-part-${index}`}
              position="relative"
              maxW="250px"
            >
              <FormComboBox
                maxW="250px"
                register={register}
                control={control}
                errors={errors}
                id={`newLines.${index}.part`}
                label="Part"
                keyForDisplay="part_number"
                keyForValue="id"
                items={filteredParts}
                showLabelAtTop
                required
              />
              <Text
                position="absolute"
                bottom={-2}
                fontSize="xs"
                color="fg.error"
              >
                {errors?.newLines?.[index]?.part?.message}
              </Text>
            </Box>
          </>
        ) : (
          <>
            <Box
              key={`new-line-manual-part-${index}`}
              position="relative"
              maxW="250px"
            >
              <FormInput
                maxW="250px"
                register={register}
                control={control}
                errors={errors}
                id={`newLines.${index}.manual_part_number`}
                label="Item / Service"
                showLabelAtTop
                required
              />
              <Text
                position="absolute"
                bottom={-2}
                fontSize="xs"
                color="fg.error"
              >
                {errors?.newLines?.[index]?.manual_part_number?.message}
              </Text>
            </Box>
            <Box maxW="300px">
              <FormInput
                maxW="300px"
                register={register}
                control={control}
                errors={errors}
                id={`newLines.${index}.description`}
                label="Description"
                showLabelAtTop
              />
            </Box>
          </>
        )}

        <Box position="relative" maxW="200px">
          <FormInput
            maxWidth="200px"
            register={register}
            control={control}
            errors={errors}
            id={`newLines.${index}.needed_by`}
            label="Needed By"
            showLabelAtTop
            type="date"
          />
          <Text position="absolute" bottom={-2} fontSize="xs" color="fg.error">
            {errors?.newLines?.[index]?.needed_by?.message}
          </Text>
        </Box>

        <Box position="relative" maxW="200px">
          <FormInput
            maxWidth="200px"
            register={register}
            control={control}
            errors={errors}
            id={`newLines.${index}.promised_date`}
            label="Promised Date"
            showLabelAtTop
            type="date"
          />
          <Text position="absolute" bottom={-2} fontSize="xs" color="fg.error">
            {errors?.newLines?.[index]?.promised_date?.message}
          </Text>
        </Box>

        <Box position="relative" maxW="120px">
          <FormInput
            maxWidth="120px"
            register={register}
            control={control}
            errors={errors}
            id={`newLines.${index}.qty`}
            label="Qty"
            showLabelAtTop
            type="number"
            min={1}
          />
          <Text position="absolute" bottom={-2} fontSize="xs" color="fg.error">
            {errors?.newLines?.[index]?.qty?.message}
          </Text>
        </Box>

        <Box position="relative" maxW="160px">
          <FormInput
            maxWidth="160px"
            register={register}
            control={control}
            errors={errors}
            id={`newLines.${index}.unit_price`}
            label="Unit Price"
            showLabelAtTop
            type="number"
            min={0}
            step="0.01"
          />
          <Text position="absolute" bottom={-2} fontSize="xs" color="fg.error">
            {errors?.newLines?.[index]?.unit_price?.message}
          </Text>
        </Box>
      </Flex>

      <FormTextArea
        register={register}
        control={control}
        errors={errors}
        id={`newLines.${index}.notes`}
        label="Notes (Optional)"
        showLabelAtTop
        minH="2lh"
        maxH="2lh"
      />
    </Flex>
  );
};

const getApiErrorStatus = (error: unknown) => {
  const status = (error as { status?: number })?.status;
  return typeof status === 'number' ? status : 500;
};

const getApiErrorDetail = (error: unknown, fallback: string) => {
  const data = (error as { data?: Record<string, unknown> })?.data;
  const detail = data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const message = detail.filter((item) => typeof item === 'string').join(' ');
    if (message) return message;
  }

  const fieldMessage = data
    ? Object.values(data)
        .flatMap((value) => (Array.isArray(value) ? value : [value]))
        .find((value): value is string => typeof value === 'string')
    : undefined;
  return fieldMessage || fallback;
};
